import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { Trash } from "react-feather";
import classNames from "classnames";
import { Formik } from "formik";
import { object, string } from "yup";
import Moment from "react-moment";
import {
  Button,
  Col,
  FormGroup,
  Input,
  InputGroup,
  Label,
  Modal,
  ModalBody,
  ModalHeader,
  Nav,
  NavItem,
  NavLink,
  Row,
  TabContent,
  Table,
  TabPane,
} from "reactstrap";

import { brandsSuggestion, createSynonym, getBrandOne, removeSynonym, updateBrand } from "services/api";
import { CloseButton } from "components/CloseButton";
import { MySpinner } from "components/MySpinner";
import { Checkbox } from "components/Checkbox";
import { TableButton } from "components/TableButton";
import { SelectSearch } from "components/SelectSearch";

import { Cross } from "../Cross";

import "./brandModal.scss";

const BrandSchema = object().shape({
  name: string().min(2).required(),
});

export const BrandModal = ({
  openModal,
  setOpenModal,
  id
}) => {

  const [loading, setLoading] = useState(false);
  const [brand, setBrand] = useState(null);
  const [synonym, setSynonym] = useState("");
  const [brandSynonym, setBrandSynonym] = useState(null);
  const [shownAddSynonym, setShownAddSynonym] = useState(false);
  const [activeTab, setActiveTab] = useState("1");

  const getAndSetBrand = () => {
    getBrandOne(id)
      .then((res) => {
        setBrand(res);
      })
      .catch((e) => console.error(e))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    getAndSetBrand();
  }, [id]);

  useEffect(() => {
    if (openModal === false) {
      setActiveTab("1");
    }
  }, [openModal]);

  const brandHandler = (values) => {
    setLoading(true);
    updateBrand(values)
      .then((res) => {
        toast.success("Бренд успешно сохранен");
      })
      .catch((e) => {
        toast.error(e.response?.data?.message);
      })
      .finally(() => setLoading(false));
  };

  const createHandler = () => {
    setLoading(true);
  };

  const removeSynonymHandler = (id) => {
    setLoading(true);
    removeSynonym({
      ids: [id]
    })
      .then((res) => {
        toast.success("Синоним успешно удален");
        getAndSetBrand();
      })
      .catch((e) => {
        toast.error(e.response?.data?.message);
        setLoading(false);
      })
      .finally(() => { });
  };

  const addSynonym = () => {
    if (!synonym) {
      toast.error("Необходимо заполнить синоним");
      return;
    }
    setLoading(true);
    createSynonym({
      brand: brand.id,
      synonym
    })
      .then((res) => {
        toast.success("Синоним успешно добавлен");
        getAndSetBrand();
        setSynonym("");
        setShownAddSynonym(false);
      })
      .catch((e) => {
        toast.error(e.response?.data?.message);
        setLoading(false);
      })
      .finally(() => { });
  };

  const brandSynonymHandler = () => {

    if (!brandSynonym) {
      toast.error("Необходимо заполнить бренд");
      return;
    }
    setLoading(true);
    createSynonym({
      brand: brandSynonym?.id,
      synonym: brand?.name
    })
      .then((res) => {
        toast.success("Синоним успешно добавлен");
        getAndSetBrand();
        setSynonym("");
        setShownAddSynonym(false);
      })
      .catch((e) => {
        toast.error(e.response?.data?.message);
        setLoading(false);
      })
      .finally(() => { });
    setBrandSynonym(null);
  }

  const toggleTab = (tab) => {
    if (activeTab !== tab) {
      setActiveTab(tab);
    }
  };

  return (
    <Modal
      isOpen={openModal}
      toggle={() => setOpenModal(false)}
      className="nomenclature-modal"
    >
      {loading && <MySpinner overlay />}
      <ModalHeader>
        <div className="modal-title-text">
          {brand?.name}
          <span className="nomenclature-modal__id">ID: {brand?.id}</span>
        </div>
        <CloseButton onClick={() => setOpenModal(false)} />
      </ModalHeader>
      <ModalBody className="m-3">
        <>
          <div className="tab">
            <Nav tabs>
              <NavItem>
                <NavLink
                  className={classNames({
                    active: activeTab === "1",
                  })}
                  onClick={() => toggleTab("1")}
                >
                  Основные данные
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink
                  className={classNames({
                    active: activeTab === "2",
                  })}
                  onClick={() => toggleTab("2")}
                >
                  Кроссы
                </NavLink>
              </NavItem>
            </Nav>
          </div>

          <TabContent activeTab={activeTab}>
            <TabPane tabId="1">
              <Formik
                initialValues={{
                  id: brand?.id || "",
                  name: brand?.name || "",
                  active: brand?.active || "",
                  synonym: brand?.synonym || [],
                  createdAt: brand?.createdAt || new Date(),
                  countNomenclature: brand?.countNomenclature || 0,
                }}
                validationSchema={BrandSchema}
                onSubmit={(values, { setSubmitting, resetForm }) => {
                  brandHandler(values, resetForm);
                  setSubmitting(false);
                }}
                enableReinitialize
              >
                {({
                  values,
                  errors,
                  touched,
                  handleChange,
                  handleBlur,
                  handleSubmit,
                  isSubmitting,
                  setFieldValue,
                }) => (
                  <form onSubmit={handleSubmit}>
                    <Row>
                      <Col md="4">
                        <Label>Активен</Label>
                        <Checkbox
                          name="active"
                          value="active"
                          checked={values.active}
                          onChange={handleChange}
                        />
                      </Col>
                      <Col md="4">
                        <Label>Дата добавления</Label>
                        <div>
                          <Moment format="DD.MM.YYYY">{values.createdAt}</Moment>
                        </div>
                      </Col>
                    </Row>
                    <Row>
                      <Col>
                        <FormGroup>
                          <Label>Наименование бренда</Label>
                          <Input
                            id="name"
                            label="Наименование бренда"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.name}
                            invalid={
                              errors.name && touched.name ? errors.name : null
                            }
                          />
                        </FormGroup>
                      </Col>
                    </Row>
                    <Row>
                      <Col>
                        <FormGroup>
                          <a
                            href={`/reference/nomenclature?brand=${values.id}&brandName=${values.name}`}
                            target="_blank"
                            rel="noreferrer"
                          > <b>Кол-во номенклатуры: {' '}{values.countNomenclature}</b>
                          </a>
                        </FormGroup>
                      </Col>
                    </Row>
                    <Row className="mb-5">
                      <Col>
                        <div className="react-bootstrap-table p-0">
                          <Table className={classNames("table table-hover pointer")}>
                            <thead>
                              <tr>
                                <th style={{ width: "90%" }}>Синоним</th>
                                <th></th>
                              </tr>
                            </thead>
                            <tbody>
                              {values?.synonym.map((item) => (
                                <tr key={item.id}>
                                  <td>{item.synonym}</td>
                                  <td>
                                    <TableButton
                                      Icon={Trash}
                                      onClick={() => removeSynonymHandler(item.id)}
                                    />
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        </div>
                        {shownAddSynonym ?
                          <div>
                            <Input
                              id="synonym"
                              label="Синоним"
                              onChange={e => setSynonym(e.target.value)}
                              value={synonym}
                              className="mb-2"
                            />
                            <div className="d-flex justify-content-end">
                              <Button onClick={addSynonym} color="primary">
                                Сохранить синоним
                              </Button>
                            </div>
                          </div>
                          :
                          <Button onClick={() => setShownAddSynonym(true)} color="primary">
                            Добавить синоним
                          </Button>
                        }
                      </Col>
                    </Row>
                    <Row className="mb-5">
                      <Col md={3}>
                        <Label>Добавить в синонимы к бренду</Label>
                        <InputGroup>
                          <SelectSearch
                            name="brandSynonym"
                            placeholder="Выберите бренд"
                            functionSuggestion={brandsSuggestion}
                            value={brandSynonym}
                            setValue={setBrandSynonym}
                            isClearable
                            className="mb-2"
                          />
                          {brandSynonym &&
                            <Button onClick={() => brandSynonymHandler()} color="primary">
                              Добавить
                            </Button>
                          }
                        </InputGroup>
                      </Col>
                    </Row>
                    <div className="d-flex justify-content-between">
                      <Button onClick={() => setOpenModal(false)} color="secondary">
                        Закрыть
                      </Button>
                      <Button color="primary">
                        Сохранить
                      </Button>
                    </div>
                  </form>
                )}
              </Formik>
            </TabPane>
            <TabPane tabId="2">
              <Cross
                activeTab={activeTab}
                brand={brand}
              />
            </TabPane>
          </TabContent>
        </>
      </ModalBody>
    </Modal>
  );
};
